import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { activityService } from '../../services/activity.service';
import { operatorService } from '../../services/operator.service';
import { contactService } from '../../services/contact.service';
import { opportunityService } from '../../services/opportunity.service';
import type {
  Activity,
  ActivityInput,
  ActivityType,
  Contact,
  Operator,
  Opportunity,
} from '../../types/crm';
import Loading from '../Loading';

const ACTIVITY_TYPES: ActivityType[] = ['call', 'email', 'meeting', 'visit', 'demo', 'other'];

interface ActivityListProps {
  // When set, scopes the list AND pre-fills the modal target. Picker hidden.
  operatorId?: number;
  contactId?: number;
  opportunityId?: number;
  // Optional default operator for the modal payload — used when the list is
  // scoped to an opportunity so newly logged activities inherit the
  // opportunity's operator without filtering the list to that operator
  // (which would hide legacy activities tagged only with opportunity_id).
  defaultOperatorId?: number;
}

const createActivitySchema = z.object({
  activity_type: z.enum(['call', 'email', 'meeting', 'visit', 'demo', 'other']),
  subject: z.string().min(1, 'Subject is required'),
  description: z.string().optional(),
  outcome: z.string().optional(),
  activity_date: z.string().min(1, 'Date is required'),
  duration_minutes: z.string().optional(),
  operator_id: z.string().optional(),
  contact_id: z.string().optional(),
  opportunity_id: z.string().optional(),
});

type CreateActivityFormData = z.infer<typeof createActivitySchema>;

const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const today = (): string => new Date().toISOString().slice(0, 10);

export const ActivityList = ({ operatorId, contactId, opportunityId, defaultOperatorId }: ActivityListProps) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Picker options (only loaded when needed, i.e. unscoped modal usage)
  const [operators, setOperators] = useState<Operator[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  const isScoped = operatorId !== undefined || contactId !== undefined || opportunityId !== undefined;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateActivityFormData>({
    resolver: zodResolver(createActivitySchema),
    defaultValues: {
      activity_type: 'call',
      activity_date: today(),
    },
  });

  const loadActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (operatorId !== undefined) params['q[operator_id_eq]'] = operatorId;
      if (contactId !== undefined) params['q[contact_id_eq]'] = contactId;
      if (opportunityId !== undefined) params['q[opportunity_id_eq]'] = opportunityId;
      const result = await activityService.getAll(params);
      setActivities(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [operatorId, contactId, opportunityId]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const openCreate = async () => {
    setShowCreateModal(true);
    reset({
      activity_type: 'call',
      activity_date: today(),
      subject: '',
      description: '',
      outcome: '',
      duration_minutes: '',
    });

    // Only load picker data when the modal is unscoped — the user needs to pick
    // a target. Scoped modal pre-fills from props and hides the pickers.
    if (!isScoped) {
      try {
        const [opsRes, conRes, oppRes] = await Promise.all([
          operatorService.getAll({ per_page: 100 }),
          contactService.getAll({ per_page: 100 }),
          opportunityService.getAll({ per_page: 100 }),
        ]);
        setOperators(opsRes.data);
        setContacts(conRes.data);
        setOpportunities(oppRes.data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const onCreate = async (data: CreateActivityFormData) => {
    setIsCreating(true);
    try {
      const input: ActivityInput = {
        activity_type: data.activity_type,
        subject: data.subject,
        description: data.description?.trim() ? data.description.trim() : null,
        outcome: data.outcome?.trim() ? data.outcome.trim() : null,
        activity_date: data.activity_date,
        duration_minutes: data.duration_minutes?.trim()
          ? parseInt(data.duration_minutes, 10)
          : null,
        operator_id:
          operatorId !== undefined
            ? operatorId
            : data.operator_id
              ? parseInt(data.operator_id, 10)
              : defaultOperatorId ?? null,
        contact_id:
          contactId !== undefined
            ? contactId
            : data.contact_id
              ? parseInt(data.contact_id, 10)
              : null,
        opportunity_id:
          opportunityId !== undefined
            ? opportunityId
            : data.opportunity_id
              ? parseInt(data.opportunity_id, 10)
              : null,
      };
      await activityService.create(input);
      toast.success('Activity logged');
      setShowCreateModal(false);
      loadActivities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Activity</h3>
        <button onClick={openCreate} className="btn btn-primary btn-sm">
          <Plus className="h-4 w-4 inline mr-1" />
          Log Activity
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : activities.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No activity yet.</div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                {operatorId === undefined && (
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                )}
                {opportunityId === undefined && (
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Opportunity</th>
                )}
                <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Outcome</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{a.activity_date}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{titleize(a.activity_type)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{a.subject}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{a.user.full_name}</td>
                  {operatorId === undefined && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{a.operator?.name || '—'}</td>
                  )}
                  {opportunityId === undefined && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{a.opportunity?.title || '—'}</td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{a.outcome || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-(--color-bg-scrim)" onClick={() => setShowCreateModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit(onCreate)}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Log activity</h3>
                    <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-500">
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="activity_type" className="label">Type</label>
                        <select {...register('activity_type')} id="activity_type" className="input">
                          {ACTIVITY_TYPES.map((t) => (
                            <option key={t} value={t}>{titleize(t)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="activity_date" className="label">Date</label>
                        <input {...register('activity_date')} id="activity_date" type="date" className="input" />
                        {errors.activity_date && <p className="mt-1 text-sm text-red-600">{errors.activity_date.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="label">Subject</label>
                      <input {...register('subject')} id="subject" type="text" className="input" />
                      {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="description" className="label">Description</label>
                      <textarea {...register('description')} id="description" rows={3} className="input" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="outcome" className="label">Outcome</label>
                        <input {...register('outcome')} id="outcome" type="text" className="input" />
                      </div>
                      <div>
                        <label htmlFor="duration_minutes" className="label">Duration (min)</label>
                        <input {...register('duration_minutes')} id="duration_minutes" type="number" min="0" className="input" />
                      </div>
                    </div>

                    {!isScoped && (
                      <>
                        <div>
                          <label htmlFor="operator_id" className="label">Operator</label>
                          <select {...register('operator_id')} id="operator_id" className="input">
                            <option value="">— none —</option>
                            {operators.map((op) => (
                              <option key={op.id} value={op.id}>{op.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="contact_id" className="label">Contact</label>
                          <select {...register('contact_id')} id="contact_id" className="input">
                            <option value="">— none —</option>
                            {contacts.map((c) => (
                              <option key={c.id} value={c.id}>{c.full_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="opportunity_id" className="label">Opportunity</label>
                          <select {...register('opportunity_id')} id="opportunity_id" className="input">
                            <option value="">— none —</option>
                            {opportunities.map((o) => (
                              <option key={o.id} value={o.id}>{o.title}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isCreating} className="btn btn-primary">
                    {isCreating ? 'Saving...' : 'Log Activity'}
                  </button>
                  <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityList;
